import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useFormParticipants } from '@/hooks/business-logic/useFormParticipants';
import useMixPanel from '@/hooks/useMixPanel';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { FieldArrayWithId, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AccordionFormItem from '../form-participant-data-accordion/components/AccordionFormItem';
import UserForm from '../form-participant-data-accordion/components/UserForm';
import ParticipantForm from './Participant';
import TermsAndConditions from '@/components/modals/termsAndConditions/TermsAndConditions';
import { Button } from '@/components/ui/button';
import { Loader } from 'lucide-react';
import { Service } from '@/services/api/services-api';
import { RegisteredUserModal } from '../../modals/registered-user-modal/RegisteredUserModal';

type Props = {
  form: UseFormReturn<any>; // TODO: Add type
  service: Service;
  fields: FieldArrayWithId<any>[];
  organization: TOrganizationFAQ;
  isLanding: boolean;
  isPendingSale?: boolean;
  addParticipant: (participant: any) => void;
  removeParticipant: (index: number) => void;
};

const ParticipantsDataFormBody = ({
  form,
  service,
  fields,
  organization,
  isLanding,
  isPendingSale,
  addParticipant,
  removeParticipant
}: Props) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);
  const {
    userData,
    emailHint,
    accordionValue,
    isUserParticipant,
    isError,
    isMaxParticipants,
    isMinParticipants,
    isOpenRegisteredUserModal,
    setIsOpenRegisteredUserModal,
    handleBlur,
    setAccordionValue,
    onBack
  } = useFormParticipants({ form, service });

  return (
    <div className="flex flex-col gap-4">
      <RegisteredUserModal
        isOpenAlertDialog={isOpenRegisteredUserModal}
        setIsOpenAlertDialog={setIsOpenRegisteredUserModal}
        organization={organization}
        email={emailHint}
        isLanding
        serviceId={service.id}
      />
      <h2 className="font-bold">Datos importantes para la reserva</h2>
      {/* User is participant Checkbox */}
      <FormField
        control={form.control}
        name={'user.userIsParticipant'}
        render={({ field }) => (
          <FormItem>
            <div className="space-y-3 p-2">
              <FormControl>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="user.userIsParticipant"
                    checked={!field.value}
                    onCheckedChange={() => {
                      Mixpanel.track('press not for me checkbox', {
                        screen: 'New Booking'
                      });
                      const isParticipant = !field.value;
                      if (!isParticipant) {
                        addParticipant({
                          firstName: '',
                          lastName: '',
                          birthdate: new Date(),
                          height: 0,
                          weight: 0,
                          id: ''
                        });
                      } else {
                        removeParticipant(0);
                      }
                      field.onChange(isParticipant);
                    }}
                  />
                  <FormLabel
                    htmlFor="user.userIsParticipant"
                    className="ml-2 cursor-pointer"
                  >
                    {t('stepParticipants.userIsParticipantLabel')}
                  </FormLabel>
                </div>
              </FormControl>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      {/* mail input */}
      <FormField
        control={form.control}
        name="user.email"
        render={({ field }) => {
          const disabled = !!userData?.email;
          return (
            <FormItem className="p-2">
              <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
              <FormControl>
                <Input
                  onClick={() =>
                    Mixpanel.track('press email input', {
                      screen: 'New Booking'
                    })
                  }
                  readOnly={disabled}
                  placeholder="example@email.com"
                  type="email"
                  {...field}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormDescription>
                {t('stepParticipants.form.mail_description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          );
        }}
      />

      <Accordion
        type="single"
        collapsible
        className="w-full space-y-6 md:container"
        onValueChange={setAccordionValue}
        value={accordionValue}
      >
        {/* User Form */}
        <AccordionFormItem
          accordionValue="participant.user"
          form={form}
          formField="user"
          setAccordionValue={setAccordionValue}
          userIsParticipant={isUserParticipant}
        >
          {(onSave) => (
            <UserForm
              form={form}
              userIsParticipant={isUserParticipant}
              user={userData}
              onSave={onSave}
            />
          )}
        </AccordionFormItem>

        {/* Participants Form */}
        {fields.map((field, index) => {
          return (
            <AccordionFormItem
              index={index}
              key={`participant.${index}.${field.id}`}
              accordionValue={`participant.${index}`}
              participantNumber={isUserParticipant ? index + 2 : index + 1}
              userIsParticipant={isUserParticipant}
              form={form}
              formField={'participants'}
              setAccordionValue={setAccordionValue}
            >
              {(onSave) => (
                <ParticipantForm
                  form={form}
                  user={userData}
                  index={index}
                  onSave={onSave}
                  isMinParticipants={isMinParticipants}
                  removeParticipant={() => removeParticipant(index)}
                />
              )}
            </AccordionFormItem>
          );
        })}
      </Accordion>

      {/* TODO: Ocultar cuando se llegue al limite de participantes */}
      {!isMaxParticipants && (
        <div className="px-6">
          <Button
            type="button"
            className="w-full"
            onClick={() => {
              addParticipant({
                firstName: '',
                lastName: '',
                birthdate: new Date(),
                height: 0,
                weight: 0,
                id: ''
              });
            }}
          >
            Agregar participante
          </Button>
        </div>
      )}

      {/* Terms and conditions  */}
      <FormField
        control={form.control}
        name="acceptedTos"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-end">
              <FormControl>
                <Checkbox
                  onClick={() =>
                    Mixpanel.track('press terms & cons', {
                      screen: 'New Booking'
                    })
                  }
                  id="isAccept"
                  checked={field.value}
                  onCheckedChange={() => field.onChange(!field.value)}
                />
              </FormControl>
              <FormLabel htmlFor="isAccept" className="ml-2">
                {t('stepParticipants.form.terms1') + ' '}
                <TermsAndConditions
                  onClose={() => field.onChange(true)}
                  organization={organization}
                />
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Declaration of legal age */}
      <FormField
        control={form.control}
        name="declarationLegalAge"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-end">
              <FormControl>
                <Checkbox
                  onClick={() =>
                    Mixpanel.track('press declarationLegalAge', {
                      screen: 'New Booking'
                    })
                  }
                  id="declarationLegalAge"
                  checked={field.value}
                  onCheckedChange={() => field.onChange(!field.value)}
                />
              </FormControl>
              <FormLabel htmlFor="declarationLegalAge" className="ml-2">
                {t('declarationLegalAge')}
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {isError && (
        <p className="text-sm font-medium text-destructive">
          {t('stepParticipants.form.errorMessage')}
        </p>
      )}
      {!isLanding && (
        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            className="w-full"
            onClick={() =>
              Mixpanel.track('press continue button', {
                screen: 'New Booking'
              })
            }
            disabled={isPendingSale}
          >
            {isPendingSale ? (
              <Loader className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              t('stepParticipants.form.continue')
            )}
          </Button>
          <Button
            onClick={() => {
              onBack();
              Mixpanel.track('press go back button', {
                screen: 'New Booking'
              });
            }}
            type="button"
            variant="outline"
            className="w-full"
          >
            {t('stepParticipants.form.back')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ParticipantsDataFormBody;
